import React, {useState} from "react";
import {Button, InputNumber, message, Modal} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash, faHeart} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";
import {updateCartItemQuantity, removeFromCart, addToFavorites, isFavorite} from "../../utils/localStorage";
import {language} from "../../language/Language";
import {useCart} from "../../Context/CartContext";

const CartItem = ({item, onUpdateCart}) => {
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [, setIsUpdating] = useState(false);

    // استخدام Context لتحديث العدادات
    const {updateCartCount, updateFavoritesCount} = useCart();

    // دالة تحديث الكمية
    const handleQuantityChange = async (newQuantity) => {
        if (!newQuantity || newQuantity < 1) return;

        setIsUpdating(true);

        // التحقق من المخزون
        const availableStock = parseFloat(item.stock) || 0;
        if (newQuantity > availableStock) {
            message.warning(`الكمية المتاحة هي ${availableStock} فقط`);
            setIsUpdating(false);
            return;
        }

        const success = updateCartItemQuantity(item.product_id, newQuantity);

        if (success) {
            onUpdateCart(); // تحديث بيانات السلة
            updateCartCount(); // تحديث عداد السلة في Context
            message.success("تم تحديث الكمية بنجاح!");
        } else {
            message.error("فشل في تحديث الكمية");
        }

        setIsUpdating(false);
    };

    // دالة إزالة من السلة
    const handleRemoveFromCart = () => {
        const success = removeFromCart(item.product_id);
        if (success) {
            onUpdateCart(); // تحديث بيانات السلة
            updateCartCount(); // تحديث عداد السلة في Context
            message.success("تم إزالة المنتج من السلة بنجاح!");
            setIsRemoveModalOpen(false);
        } else {
            message.error("فشل في إزالة المنتج من السلة");
        }
    };

    // دالة إضافة/إزالة من المفضلة
    const handleToggleFavorite = () => {
        const productForFavorites = {
            product_id: item.product_id,
            name: item.item_name,
            price: item.original_price || item.unit_price,
            default_sales_discount: item.discount || 0,
            image_url: item.icon,
            stock: item.stock,
        };

        const wasAdded = addToFavorites(productForFavorites);

        // تحديث عداد المفضلة في Context
        updateFavoritesCount();

        if (wasAdded) {
            message.success("تم إضافة المنتج إلى المفضلة!");
        } else {
            message.success("تم إزالة المنتج من المفضلة!");
        }
    };

    const isProductFavorite = isFavorite(item.product_id);
    const unitPrice = parseFloat(item.unit_price);
    const totalPrice = parseFloat(item.price);
    const originalPrice = item.original_price ? parseFloat(item.original_price) : unitPrice;
    const hasDiscount = item.discount && item.discount > 0;

    return (
        <>
            {/* Container الرئيسي - متجاوب مع جميع الشاشات */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-3 sm:p-4 mb-3 sm:mb-4">
                {/* Layout للشاشات الكبيرة - صف واحد */}
                <div className="hidden md:flex items-center gap-4">
                    {/* صورة المنتج */}
                    <div className="flex-shrink-0">
                        <Link to={`/Products/${item.product_id}`}>
                            <img
                                src={item.icon || "https://test.prosofteg.com/img/default.png"}
                                alt={item.item_name}
                                className="w-20 h-20 xl:w-24 xl:h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.src = "https://test.prosofteg.com/img/default.png";
                                }}
                            />
                        </Link>
                    </div>

                    {/* تفاصيل المنتج */}
                    <div className="flex-grow min-w-0">
                        <Link to={`/Products/${item.product_id}`} className="text-decoration-none">
                            <h4 className="text-lg xl:text-xl font-semibold text-gray-800 hover:text-blue-600 transition-colors line-clamp-2">
                                {item.item_name}
                            </h4>
                        </Link>

                        {/* معلومات السعر */}
                        <div className="flex items-center gap-2 mt-2">
                            <span className="text-lg xl:text-xl font-bold text-mainColor">
                                EGP {unitPrice.toFixed(0)}
                            </span>
                            {hasDiscount && (
                                <>
                                    <span className="text-sm text-gray-500 line-through">
                                        EGP {originalPrice.toFixed(0)}
                                    </span>
                                    <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                                        {item.discount}% خصم
                                    </span>
                                </>
                            )}
                        </div>

                        {/*هنا تم ايقاف الكمية من المنتج */}
                        {/* معلومات المخزون */}
                        {/* <div className="text-sm text-gray-500 mt-1">
                            {language === "ar" ? "متوفر:" : "Available:"} {parseFloat(item.stock).toFixed(0)}{" "}
                            {language === "ar" ? "قطعة" : "pieces"}
                        </div> */}
                        {/*هنا تم ايقاف الكمية من المنتج */}
                    </div>

                    {/* التحكم في الكمية والإجراءات */}
                    <div className="flex items-center gap-4">
                        {/* تحديد الكمية */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                                {language === "ar" ? "الكمية:" : "Qty:"}
                            </span>
                            <InputNumber
                                min={1}
                                max={parseFloat(item.stock)}
                                value={item.count}
                                onChange={handleQuantityChange}
                                size="middle"
                                className="w-20 h-10"
                            />
                        </div>

                        {/* إجمالي السعر */}
                        <div className="text-center min-w-[80px]">
                            <div className="text-lg xl:text-xl font-bold text-gray-800">
                                EGP {totalPrice.toFixed(0)}
                            </div>
                            <div className="text-xs text-gray-500">{language === "ar" ? "الإجمالي" : "Total"}</div>
                        </div>

                        {/* أزرار الإجراءات */}
                        <div className="flex flex-col gap-2">
                            {/* زر المفضلة */}
                            <Button
                                type="text"
                                size="small"
                                onClick={handleToggleFavorite}
                                className={`${isProductFavorite ? "text-red-500" : "text-gray-500"} hover:text-red-500`}
                                icon={<FontAwesomeIcon icon={faHeart} />}
                                title={isProductFavorite ? "إزالة من المفضلة" : "إضافة للمفضلة"}
                            />

                            {/* زر الحذف */}
                            <Button
                                type="text"
                                size="small"
                                danger
                                onClick={() => setIsRemoveModalOpen(true)}
                                icon={<FontAwesomeIcon icon={faTrash} />}
                                title="حذف من السلة"
                            />
                        </div>
                    </div>
                </div>

                {/* Layout للشاشات المتوسطة والصغيرة - تخطيط عمودي */}
                <div className="md:hidden">
                    {/* الصف الأول: الصورة والمعلومات الأساسية */}
                    <div className="flex gap-3 mb-4">
                        {/* صورة المنتج */}
                        <div className="flex-shrink-0">
                            <Link to={`/Products/${item.product_id}`}>
                                <img
                                    src={item.icon || "https://test.prosofteg.com/img/default.png"}
                                    alt={item.item_name}
                                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                    loading="lazy"
                                    onError={(e) => {
                                        e.target.src = "https://test.prosofteg.com/img/default.png";
                                    }}
                                />
                            </Link>
                        </div>

                        {/* تفاصيل المنتج */}
                        <div className="flex-grow min-w-0">
                            <Link to={`/Products/${item.product_id}`} className="text-decoration-none">
                                <h4 className="text-base sm:text-lg font-semibold text-gray-800 hover:text-blue-600 transition-colors line-clamp-2 leading-tight">
                                    {item.item_name}
                                </h4>
                            </Link>

                            {/* معلومات السعر */}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                <span className="text-lg font-bold text-mainColor">EGP {unitPrice.toFixed(0)}</span>
                                {hasDiscount && (
                                    <>
                                        <span className="text-sm text-gray-500 line-through">
                                            EGP {originalPrice.toFixed(0)}
                                        </span>
                                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                                            {item.discount}% خصم
                                        </span>
                                    </>
                                )}
                            </div>

                            {/*هنا تم ايقاف الكمية من المنتج */}
                            {/* معلومات المخزون */}
                            {/* <div className="text-sm text-gray-500 mt-1">
                                {language === "ar" ? "متوفر:" : "Available:"} {parseFloat(item.stock).toFixed(0)}{" "}
                                {language === "ar" ? "قطعة" : "pieces"}
                            </div> */}
                            {/*هنا تم ايقاف الكمية من المنتج */}
                        </div>
                    </div>

                    {/* الصف الثاني: التحكم في الكمية والإجراءات */}
                    <div className="flex flex-col xs:flex-row items-stretch xs:items-center justify-between gap-3 pt-3 border-t border-gray-100">
                        {/* تحديد الكمية وإجمالي السعر */}
                        <div className="flex items-center justify-between xs:justify-start xs:gap-4 flex-grow">
                            {/* تحديد الكمية */}
                            <div className="flex items-center gap-2">
                                <button onClick={() => handleQuantityChange(item.count - 1)}>-</button>

                                <span className="text-lg text-gray-600 whitespace-nowrap">
                                    {language === "ar" ? "الكمية:" : "Qty:"}
                                </span>
                                <button onClick={() => handleQuantityChange(item.count + 1)}>+</button>

                                <InputNumber
                                    min={1}
                                    max={parseFloat(item.stock)}
                                    value={item.count}
                                    onChange={handleQuantityChange}
                                    size="small"
                                    className="w-20 h-10 sm:w-22 sm:h-20 "
                                />
                            </div>

                            {/* إجمالي السعر */}
                            <div className="text-right xs:text-center">
                                <div className="text-base sm:text-lg font-bold text-gray-800">
                                    EGP {totalPrice.toFixed(0)}
                                </div>
                                <div className="text-xs text-gray-500">{language === "ar" ? "الإجمالي" : "Total"}</div>
                            </div>
                        </div>

                        {/* أزرار الإجراءات */}
                        <div className="flex justify-center xs:justify-end gap-2 flex-shrink-0">
                            {/* زر المفضلة */}
                            <Button
                                type="text"
                                size="middle"
                                onClick={handleToggleFavorite}
                                className={`${
                                    isProductFavorite ? "text-red-500" : "text-gray-500"
                                } hover:text-red-500 flex-1 xs:flex-none min-w-[44px] h-[44px] flex items-center justify-center`}
                                icon={<FontAwesomeIcon icon={faHeart} size="lg" />}
                                title={isProductFavorite ? "إزالة من المفضلة" : "إضافة للمفضلة"}
                            />

                            {/* زر الحذف */}
                            <Button
                                type="text"
                                size="middle"
                                danger
                                onClick={() => setIsRemoveModalOpen(true)}
                                icon={<FontAwesomeIcon icon={faTrash} size="lg" />}
                                title="حذف من السلة"
                                className="flex-1 xs:flex-none min-w-[44px] h-[44px] flex items-center justify-center"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal تأكيد الحذف */}
            <Modal
                title={language === "ar" ? "تأكيد الحذف" : "Confirm Removal"}
                open={isRemoveModalOpen}
                onCancel={() => setIsRemoveModalOpen(false)}
                centered
                className="sm:max-w-md"
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => setIsRemoveModalOpen(false)}
                        className="w-full sm:w-auto mb-2 sm:mb-0"
                    >
                        {language === "ar" ? "إلغاء" : "Cancel"}
                    </Button>,
                    <Button
                        key="remove"
                        type="primary"
                        danger
                        onClick={handleRemoveFromCart}
                        className="w-full sm:w-auto"
                    >
                        {language === "ar" ? "حذف" : "Remove"}
                    </Button>,
                ]}
            >
                <p className="text-center sm:text-right">
                    {language === "ar"
                        ? `هل أنت متأكد من حذف "${item.item_name}" من السلة؟`
                        : `Are you sure you want to remove "${item.item_name}" from the cart?`}
                </p>
            </Modal>
        </>
    );
};

export default CartItem;
